import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Select } from "antd";

import PageHeader from "@/components/UI/PageHeader";
import Loader from "@/components/UI/Loader";
import {
  XYLinePlot,
  type XYSeries,
} from "@/components/Waveforms/shared/XYLinePlot";
import {
  OVERLAY_ECG_MAX_POINTS,
  useEcgStudyIndexQuery,
  useEcgWaveformQuery,
} from "@/hooks/useWaveformQueries";
import { ECG_LEAD_NAMES } from "@/components/Waveforms/EcgWaveformViewer/constants";
import type { EcgStudyIndexEntry } from "@/components/Waveforms/types";

/**
 * ECG Trends — per-subject comparative waveform view (Michael ask 1a, "overlay across visits").
 *
 * Pick a subject and a lead; the subject's ECG for that lead is overlaid across ALL of their
 * visits on one time axis, so morphology/interval change over time is directly comparable.
 * Reuses the existing waveform endpoints (`useEcgStudyIndexQuery` / `useEcgWaveformQuery`) and the
 * shared multi-series `XYLinePlot` — no new backend or data. (Spirometry gets the same treatment
 * next, keyed by subject + effort instead of imaging_study.)
 */

const PALETTE = [
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#eb2f96",
  "#722ed1",
  "#13c2c2",
  "#f5222d",
  "#a0d911",
  "#fa541c",
  "#2f54eb",
];

/** Loads one study's chosen lead and reports it up as a series (keyed by study+lead). Renders nothing. */
function EcgSeriesLoader({
  study,
  lead,
  color,
  onSeries,
}: {
  study: EcgStudyIndexEntry;
  lead: string;
  color: string;
  onSeries: (seriesKey: string, series: XYSeries) => void;
}) {
  const { data } = useEcgWaveformQuery({
    imagingStudyId: study.imaging_study_id,
    leads: [lead],
    // One of these per visit lands on a single shared plot — see the constant.
    maxPoints: OVERLAY_ECG_MAX_POINTS,
  });

  useEffect(() => {
    if (!data) return;
    const leadSamples =
      data.leads.find((l) => l.lead_name === lead) ?? data.leads[0];
    if (!leadSamples) return;
    const points = data.times_ms
      .map((t, i) => ({ x: t, y: leadSamples.amplitude_uv[i] }))
      .filter((p) => p.y != null && !Number.isNaN(p.y));
    onSeries(`${study.imaging_study_id}::${lead}`, {
      key: study.imaging_study_id,
      label: study.visit_type ?? study.study_date ?? "Visit",
      color,
      points,
    });
  }, [data, lead, color, study, onSeries]);

  return null;
}

/**
 * @param embedded When true the component drops its own page header and outer
 *   page padding, so it can render as a view inside another screen (the ECG
 *   screen's "ECG Trends" tab) without a redundant second header.
 */
export default function EcgVisitTrends({
  embedded = false,
}: {
  embedded?: boolean;
} = {}) {
  const { data: index, isLoading, isError } = useEcgStudyIndexQuery();

  const subjects = useMemo(
    () =>
      Array.from(
        new Set(
          (index ?? []).map((e) => e.subject).filter((s): s is string => !!s),
        ),
      ).sort(),
    [index],
  );

  const [subject, setSubject] = useState<string | null>(null);
  const activeSubject = subject ?? subjects[0] ?? null;
  const [lead, setLead] = useState("II");

  // The subject's ECG studies, one per visit, in chronological order.
  const studies = useMemo(
    () =>
      (index ?? [])
        .filter((e) => e.subject === activeSubject)
        .sort((a, b) => (a.study_date ?? "").localeCompare(b.study_date ?? "")),
    [index, activeSubject],
  );

  // Series accumulate keyed by study+lead, so switching lead/subject never shows stale traces
  // (no reset effect needed — avoids the child-before-parent effect-order race).
  const [seriesMap, setSeriesMap] = useState<Record<string, XYSeries>>({});
  const onSeries = useCallback(
    (seriesKey: string, series: XYSeries) =>
      setSeriesMap((m) => ({ ...m, [seriesKey]: series })),
    [],
  );

  const series = useMemo(
    () =>
      studies
        .map((s) => seriesMap[`${s.imaging_study_id}::${lead}`])
        .filter(Boolean) as XYSeries[],
    [studies, seriesMap, lead],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader />
      </div>
    );
  }
  return (
    <div className={`w-full flex flex-col gap-4${embedded ? "" : " p-4"}`}>
      {!embedded && (
        <PageHeader
          title="Measures: ECG Trends"
          tooltipText="Overlay a subject's ECG for one lead across all of their visits, to compare morphology and intervals over time."
        />
      )}

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load ECG studies"
          description="Something went wrong while fetching ECG waveform data. Please try again later."
        />
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[13px] text-gray-500">Subject</span>
            <Select
              showSearch
              className="min-w-[160px]"
              placeholder="Select subject"
              value={activeSubject ?? undefined}
              onChange={(v) => setSubject(v)}
              options={subjects.map((s) => ({ label: s, value: s }))}
            />
            <span className="text-[13px] text-gray-500 ml-2">Lead</span>
            <Select
              className="min-w-[90px]"
              value={lead}
              onChange={setLead}
              options={ECG_LEAD_NAMES.map((l) => ({ label: l, value: l }))}
            />
            {activeSubject && (
              <span className="text-[13px] text-gray-400 ml-2">
                {studies.length} visit{studies.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {/* Data loaders — one per visit; render nothing, feed series up. */}
          {studies.map((s, i) => (
            <EcgSeriesLoader
              key={s.imaging_study_id}
              study={s}
              lead={lead}
              color={PALETTE[i % PALETTE.length]}
              onSeries={onSeries}
            />
          ))}

          {studies.length === 0 ? (
            <Alert
              type="info"
              showIcon
              message="No ECG studies for this subject."
            />
          ) : series.length === 0 ? (
            <div className="flex justify-center items-center py-10">
              <Loader />
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-2">
              <XYLinePlot
                series={series}
                xLabel="Time (ms)"
                yLabel="Amplitude (µV)"
                height={440}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
